import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDrag, useDrop, DndProvider, DropTargetMonitor, DragSourceMonitor } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
// @ts-ignore - HTML5Backend type compatibility issue with react-dnd versions
import update from 'immutability-helper';
import { useDispatch } from 'react-redux';
import { setModuleOrderSequence } from '../Store/SystemMasters.action';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import { getModuleOrderIconComponent } from '../../../constants/module-order-icon-registry';
import type { ModuleItemsSidebarProps, DraggableItemProps, DragItem, ModuleItem } from './ModuleOrder.types';
import '../../../Pages/Library/CustomModule/system-canvas-cm.css';
import './ModuleOrder.css';

/** Fallback icon when API does not provide one. */
const DEFAULT_ITEM_ICON = 'icon-BOM-Library';

/** Item glyphs render on the 20px General icon box. */
const ITEM_ICON_SIZE = 20;

const getItemIcon = (item: ModuleItem): string => {
  const icon = item?.['icon'] ?? item?.['iconName'];
  if (icon && typeof icon === 'string') return icon;
  return DEFAULT_ITEM_ICON;
};

const isIconUrl = (value: unknown): boolean => {
  if (value == null || typeof value !== 'string') return false;
  const trimmed = (value as string).trim();
  return trimmed.length > 0 && (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//'));
};

const DraggableItem: React.FC<DraggableItemProps> = ({ id, item, index, moveCard }) => {
  const ref = useRef<HTMLDivElement>(null);
  const dropResult: [{ handlerId: string | symbol | null }, (node: HTMLDivElement | null) => void] = useDrop<
    DragItem,
    unknown,
    { handlerId: string | symbol | null }
  >({
    accept: 'module-item',
    collect(monitor: DropTargetMonitor<DragItem, unknown>): { handlerId: string | symbol | null } {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(draggedItem: DragItem, monitor: DropTargetMonitor<DragItem, unknown>) {
      if (!ref.current) {
        return;
      }
      const dragIndex = draggedItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveCard(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });
  const [{ handlerId }, drop] = dropResult;
  const [{ isDragging }, drag] = useDrag<DragItem, unknown, { isDragging: boolean }>({
    type: 'module-item',
    item: (): DragItem => {
      return { id, index };
    },
    collect: (monitor: DragSourceMonitor<DragItem, unknown>): { isDragging: boolean } => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const combinedRef = (node: HTMLDivElement | null) => {
    drop(node);
    drag(node);
    ref.current = node;
  };

  const iconValue = getItemIcon(item);
  const IconComponent = getModuleOrderIconComponent(iconValue);
  const iconIsUrl = isIconUrl(iconValue);

  /**
   * Draws the item glyph. A registered SVG component wins, then an uploaded icon URL,
   * then the legacy icomoon class kept for configs not yet migrated.
   * @returns {JSX.Element} The icon element.
   */
  const renderItemIcon = () => {
    if (IconComponent) return <IconComponent size={ITEM_ICON_SIZE} className="mr-2" />;
    if (iconIsUrl) {
      return <img src={(iconValue as string).trim()} alt={item.name || ''} className="item-icon-img f-20 mr-2" referrerPolicy="no-referrer" />;
    }
    return <i className={`${iconValue} f-20 text-grey mr-2 item-icons`} />;
  };

  return (
    <div ref={combinedRef} className={`draggable-item ${isDragging ? 'dragging' : ''}`} data-handler-id={handlerId}>
      <div className="module-item-row d-flex align-items-center justify-content-between p-2">
        <div className="d-flex align-items-center flex-grow-1">
          {renderItemIcon()}
          <span className="f-14">{item.name}</span>
        </div>
      </div>
      <i className="icon-Rearrange f-24 mr-2 drag-handle" />
    </div>
  );
};

const ModuleItemsSidebar: React.FC<ModuleItemsSidebarProps> = ({ show, onHide, module, moduleItems, onSaveSequence }) => {
  const { t } = useTranslation(['systemMaster', 'common']);
  const dispatch = useDispatch();
  const [rearrangeData, setRearrangeData] = useState<ModuleItem[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (moduleItems && moduleItems.length > 0) {
      const itemsWithSequence: ModuleItem[] = moduleItems.map((item, index) => ({
        ...item,
        _id: item._id || `item-${index}`,
        name: item.name || item.title || item.label || String(item),
        sequence: item.sequence || index + 1,
      }));
      const sortedItems = [...itemsWithSequence].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
      setRearrangeData(sortedItems);
    } else if (module?.items) {
      const itemsFromModule: ModuleItem[] = module.items.map((item, index) => ({
        _id: `item-${index}`,
        name: item,
        sequence: index + 1,
      }));
      setRearrangeData(itemsFromModule);
    }
    setHasUnsavedChanges(false);
  }, [moduleItems, module]);

  const moveCard = (dragIndex: number, hoverIndex: number): void => {
    const dragRecord = rearrangeData[dragIndex];
    if (!dragRecord) return;
    setHasUnsavedChanges(true);
    setRearrangeData(
      update(rearrangeData, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord],
        ],
      }),
    );
  };

  const handleSave = async (): Promise<void> => {
    try {
      const updatedSequence = rearrangeData.map((item, i) => ({
        _id: item._id || `item-${i}`,
        sequence: i + 1,
      }));
      if (onSaveSequence && module) {
        await onSaveSequence(module.id, updatedSequence);
      } else if (module) {
        await dispatch(setModuleOrderSequence({ moduleId: module.id, sequence: updatedSequence }));
      }
      setHasUnsavedChanges(false);
      toasterMsg(t('MODULE_ORDER.SEQUENCE_SAVED_SUCCESS'), 'success');
    } catch (error) {
      toasterMsg(t('MODULE_ORDER.SEQUENCE_SAVE_ERROR'), 'danger');
    }
  };

  if (!module) return null;

  return (
    <div
      className={`offcanvas offcanvas-end${show ? ' show' : ''}`}
      style={{ visibility: show ? 'visible' : 'hidden' }}
      tabIndex={-1}
      aria-labelledby="moduleOrderSidebarLabel"
      aria-hidden={!show}
      role="dialog"
    >
      <div className="offcanvas-header">
        <div className="d-inline-flex align-items-center">
          {hasUnsavedChanges && (
            <button className="btn btn-primary h-24px py-0 px-2 bg-1e90ff ml-2" type="button" onClick={handleSave} disabled={!rearrangeData.length}>
              {t('COMMON_LABEL.SAVE', { ns: 'common' })}
            </button>
          )}
        </div>
        <h4 className="mx-auto my-0 text-bold" id="moduleOrderSidebarLabel">
          {module.name}
        </h4>
        <i className="icon-close f-20 cursor-pointer" onClick={onHide}></i>
      </div>
      <div className="offcanvas-body">
        {/* @ts-ignore - HTML5Backend type compatibility issue */}
        <DndProvider backend={HTML5Backend}>
          <div className="module-items-list">
            {rearrangeData.length > 0 ? (
              rearrangeData.map((item, index) => (
                <DraggableItem key={item._id || index} id={item._id || index} item={item} index={index} moveCard={moveCard} moduleId={module?.id} />
              ))
            ) : (
              <div className="text-center p-4 text-muted">{t('MODULE_ORDER.NO_ITEMS')}</div>
            )}
          </div>
        </DndProvider>
      </div>
    </div>
  );
};

export default ModuleItemsSidebar;
